import { marketMaker } from '@/lib/api/client';

// ---------------------------------------------------------------------------
// Wire contracts.
// Decimal quantities are strings; timestamps are ISO strings unless noted.
// ---------------------------------------------------------------------------

/** Command vocabulary (requests). There is no 'running' command. */
export type MarketMakerState = 'start' | 'pause' | 'stop';

/**
 * Status vocabulary (responses). The legacy status→colour switch handles exactly
 * these five and falls back to grey for anything else — keep the union open.
 */
export type MakerStatus = 'ready' | 'starting' | 'paused' | 'stopped' | 'error' | (string & Record<never, never>);

export interface TimelinePoint {
  /** minutes (integer; editor snaps to whole minutes) */
  t: number;
  /** percent change vs current mid price (editor snaps to 0.5 steps) */
  changePct: number;
}

// GET {MM}/api/markets

export interface TakerStatus {
  accountId: string;
  status: MakerStatus;
}

export interface MarketStatus {
  /** e.g. "BTC_EUR" */
  market: string;
  makerStatus: MakerStatus;
  /** may be empty (market has no takers) */
  takerStatuses: TakerStatus[];
  /** decimal string, null before first tick */
  lastMidPrice: string | null;
  hasSimulation: boolean;
}

export interface MarketsResponse {
  markets: MarketStatus[];
}

// GET {MM}/api/accounts/balances

export interface AccountBalanceRow {
  /** e.g. "BTC" */
  currency: string;
  /** decimal string */
  available: string;
  /** decimal string */
  locked: string;
}

export interface AccountPosition {
  /** e.g. "BTC_EUR_P" */
  symbol: string;
  /** 'LONG' | 'SHORT' observed; UI only special-cases 'LONG' */
  direction: string;
  /** decimal string */
  openVolume: string;
}

export interface AccountBalance {
  id: string;
  name: string;
  /** 'maker' | 'taker' observed; UI only special-cases 'maker' */
  role: string;
  /** markets this account trades */
  markets: string[];
  balances: AccountBalanceRow[];
  positions: AccountPosition[];
}

export interface BalancesResponse {
  accounts: AccountBalance[];
}

// GET {MM}/actuator/info — bare object, no envelope

export interface AppInfo {
  /** rendered as "v{version}" */
  version: string;
  /** e.g. "development" */
  environment: string;
  uptimeMs: number;
  /** pre-formatted by the service, e.g. "2h 13m" — render verbatim */
  uptimeHuman: string;
  /** ISO timestamp */
  startedAt: string;
}

// GET {MM}/api/simulation

export interface SimulationEntry {
  /** 'driver' | 'follower' observed; rendered verbatim in a chip */
  role: string;
  /** scenario name as submitted */
  scenario: string;
  /** seconds */
  startDelay: number;
  /** epoch ms (not rendered by the legacy UI) */
  startTime: number;
  /** ISO; rendered when present */
  estimatedStartTime?: string;
  /** ISO; rendered next to estimatedStartTime */
  estimatedEndTime?: string;
}

/**
 * Keyed by MARKET id, one entry per market participating in a simulation
 * (a driver + its followers each appear as their own key).
 */
export type SimulationsResponse = Record<string, SimulationEntry>;

// POST {MM}/api/simulation

export interface StartSimulationRequest {
  /** free-text scenario name */
  scenario: string;
  /** SECONDS before the run starts (timeline t values are MINUTES — by design) */
  startDelay: number;
  driver: {
    market: string;
    /** sorted by t asc; t in minutes from sim start */
    timeline: TimelinePoint[];
  };
  /** [] when none; entries with empty market are filtered out client-side before submit */
  followers: { market: string; beta: number }[];
  /** OMITTED entirely when the recovery series is empty */
  recovery?: {
    /** t in minutes RELATIVE TO DRIVER END */
    timeline: TimelinePoint[];
  };
}

// DELETE {MM}/api/simulation — with a JSON BODY (the service reads the body)

export interface CancelSimulationRequest {
  /** cancel the simulation touching this market */
  market?: string;
  /** cancel by scenario name (never sent by the UI) */
  scenario?: string;
}

// POST {MM}/api/markets/state, {MM}/api/markets/taker/state

export interface SetStateRequest {
  state: MarketMakerState;
}

// ---------------------------------------------------------------------------
// Fetchers — success on the mutations = any non-error HTTP status; bodies unused.
// ---------------------------------------------------------------------------

export const getMarkets = () => marketMaker.get('api/markets').json<MarketsResponse>();

export const getBalances = () => marketMaker.get('api/accounts/balances').json<BalancesResponse>();

// ⚠️ actuator/info is NOT under /api
export const getAppInfo = () => marketMaker.get('actuator/info').json<AppInfo>();

export const getSimulations = () => marketMaker.get('api/simulation').json<SimulationsResponse>();

export const startSimulation = async (body: StartSimulationRequest): Promise<void> => {
  await marketMaker.post('api/simulation', { json: body });
};

// DELETE with a JSON body — do NOT move these to query params
export const cancelSimulation = async (body: CancelSimulationRequest): Promise<void> => {
  await marketMaker.delete('api/simulation', { json: body });
};

/** `market` omitted = apply to ALL markets (the Start/Pause/Stop All buttons). */
export const setMakerState = async (state: MarketMakerState, market?: string): Promise<void> => {
  await marketMaker.post('api/markets/state', {
    json: { state } satisfies SetStateRequest,
    ...(market ? { searchParams: { market } } : {}),
  });
};

/** Identical contract to setMakerState but controls the taker bots. */
export const setTakerState = async (state: MarketMakerState, market?: string): Promise<void> => {
  await marketMaker.post('api/markets/taker/state', {
    json: { state } satisfies SetStateRequest,
    ...(market ? { searchParams: { market } } : {}),
  });
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DUST_THRESHOLD = 0.01;
const DUST_DECIMALS = 6;
const BALANCE_MAX_FRACTION_DIGITS = 4;

// Port of legacy formatBalance() — balance amounts, locked amounts, position volumes.
// (Market-card lastMidPrice deliberately renders RAW — never through this.)
export const formatBalance = (value: string): string => {
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return value; // non-numeric → render raw string
  if (Math.abs(num) < DUST_THRESHOLD) return num.toFixed(DUST_DECIMALS); // dust → 6 decimals (includes 0)
  return num.toLocaleString(undefined, { maximumFractionDigits: BALANCE_MAX_FRACTION_DIGITS });
};

/**
 * Dialog price-poll extraction (legacy useMarketMakerPrice): null before the first
 * tick or when the market is missing; Number.isFinite guards a parse failure.
 */
export const extractMidPrice = (response: MarketsResponse, market: string): number | null => {
  const found = response.markets.find((entry) => entry.market === market);
  if (!found?.lastMidPrice) return null;
  const price = Number.parseFloat(found.lastMidPrice);
  return Number.isFinite(price) ? price : null;
};
